import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Form from "react-bootstrap/Form";
import "./signature.css";
import SendSignDocumentRequestTable from "./SendSignDocumentRequestTable";
import InboxTable from "./InboxTable";
import DateRangePicker from "./DateRangePicker";
import { GetCountOfCIRAsync } from "../../../services/participantServices/ParticipantService";
import { useAuth } from "../../../firebase/AuthProvider";
import { GetCountOfCIR } from "../../../interfaces/Participant";
import Pagination from "../../ui/Pagination/Pagination";
import {
  getAllSignatureRequests,
  getSignatureInboxRequests,
} from "../../../services/fileServices/signatures/SignatureService";
import { SignatureRequestQueryReq } from "../../../interfaces/file/fileSignature/SignatureRequest";
import '../TasksPage/TasksPage.css'
interface DropdownPosition {
  top: number;
  left: number;
}

export default function SignDocument() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const eventKeyFromQuery = params.get("eventkey");
  const { currentUser } = useAuth();

  const [activeTab, setActiveTab] = useState(
    eventKeyFromQuery === "inbox" ? "inbox" : "sent"
  );
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [count, setCount] = useState<GetCountOfCIR>();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>({
    top: 0,
    left: 0,
  });

  // Common pagination state
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
  });
  const [totalCount, setTotalCount] = useState(0);
  // const [inboxCount, setInboxCount] = useState(0);
  const [sentCount, setSentCount] = useState(0);

  const statusOptions = ["All Status", "Pending", "Completed", "Rejected"];

  // Fetch signature count on component mount
  useEffect(() => {
    currentUser?.getIdToken().then((token) => {
      if (token) {
        getCountOfCIR(currentUser.uid, token);
        fetchInitialCounts(token, currentUser.uid);
      }
    });
  }, [currentUser]);

  const getCountOfCIR = (userId: string, token: string) => {
    GetCountOfCIRAsync(token, userId).then((res) => {
      if (res.success) {
        setCount(res.data);
      }
    });
  };

  // Fetch initial counts for both inbox and sent tabs
  const fetchInitialCounts = async (token: string, userId: string) => {
    try {
      // Fetch sent count
      const sentPayload: SignatureRequestQueryReq = {
        ownerId: userId,
        
        signStatus: 'pending',
        pageNumber: 1,
        pageSize: 1,
      };
      const sentResult = await getAllSignatureRequests(token, sentPayload);
   
        setTotalCount(sentResult?.totalCount || 0)
        setSentCount(sentResult?.totalCount || 0);
      

      // Fetch inbox count
      const inboxResult = await getSignatureInboxRequests(
        token,
        userId,
        1,
        1,
        ""
      );
      if (inboxResult) {
        // Filter by pending status and date range if provided
        let inboxItems = inboxResult.items || [];
        // Filter to only pending status items
        inboxItems = inboxItems.filter((doc: any) => {
          return doc.status?.toLowerCase() === "pending";
        });
        // Filter by date range if provided
        if (startDate || endDate) {
          inboxItems = inboxItems.filter((doc: any) => {
            if (!doc.createdAt) return false;
            try {
              const createdDate = new Date(doc.createdAt);
              createdDate.setHours(0, 0, 0, 0);

              if (startDate && endDate) {
                const start = new Date(startDate);
                start.setHours(0, 0, 0, 0);
                const end = new Date(endDate);
                end.setHours(23, 59, 59, 999);
                return createdDate >= start && createdDate <= end;
              } else if (startDate) {
                const start = new Date(startDate);
                start.setHours(0, 0, 0, 0);
                return createdDate >= start;
              } else if (endDate) {
                const end = new Date(endDate);
                end.setHours(23, 59, 59, 999);
                return createdDate <= end;
              }
            } catch (error) {
              return false;
            }
            return true;
          });
        }
        // setInboxCount(inboxItems.length);
      }
    } catch (error) {
      console.error("Error fetching initial counts:", error);
    }
  };

  // Refetch counts when date range changes
  useEffect(() => {
    if (currentUser) {
      currentUser.getIdToken().then((token) => {
        if (token) {
          fetchInitialCounts(token, currentUser.uid);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const handleClick = () => {
    if (!isOpen && buttonRef.current && dropdownRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const parentRect = dropdownRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: buttonRect.bottom - parentRect.top + 2,
        left: buttonRect.left - parentRect.left,
      });
    }
    setIsOpen(!isOpen);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (option: string) => {
    setStatusFilter(option);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const tabs = [
    { id: "sent", label: "Sent", count: sentCount || 0 },
    { id: "inbox", label: "Inbox", count: count?.countOfFileForReview || 0 },
  ] as const;

  const navigate = useNavigate();
  const handleDateRangeChange = (start: Date | null, end: Date | null) => {
    setStartDate(start);
    setEndDate(end);
    // Reset to page 1 when date range changes
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  };

  // Reset to page 1 when search term changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  }, [searchTerm]);

  // Reset to page 1 when status filter changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  }, [statusFilter]);

  // Reset to page 1 when tab changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));
  }, [activeTab]);

  const handlePageChange = (pageNum: number) => {
    setPagination((prev) => ({
      ...prev,
      pageNumber: pageNum,
    }));
  };

  const handleResetPageSize = (pageNumber: number, pageSize: number) => {
    setPagination({
      pageNumber: pageNumber,
      pageSize: pageSize,
    });
  };

  const handleTotalCountChange = (count: number) => {
    // setTotalCount(count);
    // // Track counts separately for each tab
    // if (activeTab === "inbox") {
    //   setInboxCount(count);
    // } else if (activeTab === "sent") {
    //   setSentCount(count);
    // }
  };

  const totalPages = Math.ceil(totalCount / pagination.pageSize);

  return (
    <>
      <div className=" drive-context-container">
        <div className="top-bar">
          <div className="breadcrumbs">
            <span
              className="cursor-pointer text-[#7e57c2]"
              onClick={() => navigate("/home")}
            >
              Dashboard
            </span>
            <span className="breadcrumb-separator">{">"}</span>
            <span className="breadcrumb-active text-[#364153]">Signatures</span>
          </div>
        </div>

        <div className="signature-document-wrapper">
          {/* Header with Tabs in Middle */}
          <header className="signature-main-header">
          <div
                className="flex max-w-[16.375rem] h-[2.5rem] rounded-[0.5rem]  items-center justify-center gap-2.5 px-[26px] py-3 relative bg-[#f9f6ff]  border border-solid border-[#efe9fa]"
                data-model-id="459:2733"
                role="tablist"
                aria-label="Message tabs"
              >
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`relative h-9 mt-[-10.00px] mb-[-10.00px] rounded-lg ${
                      tab.id === "sent"
                        ? "w-[124px] ml-[-25.00px]"
                        : "w-[126px] mr-[-25.00px]"
                    } ${
                      activeTab === tab.id
                        ? "bg-white active shadow-[0px_1px_2px_#0000000f] text-[#7E57C2]"
                        : "bg-transparent text-[#747474]"
                    }`}
                    onClick={() => setActiveTab(tab.id)}
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    aria-controls={`${tab.id}-panel`}
                    type="button"
                  >
                    <div className="flex items-center gap-2 justify-center ">
                      <span className="">{tab.label}</span>

                      <span
                        className={`count-tab ${tab.label}  ${
                          activeTab === tab.id ? "active:" : "not-active"
                        }`}
                        aria-label={`${tab.count || 0} messages `}
                      >
                        <span
                          className={`count-tab-text ${tab.label}   ${
                            activeTab === tab.id ? "active:" : "not-active"
                          }`}
                        >
                          {tab.count as number || 0}
                        </span>
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            {/* Search Input */}
            <div className="signature-search-field">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="19"
                height="19"
                viewBox="0 0 19 19"
                fill="none"
                className="signature-search-icon mb-[2px]"
              >
                <path
                  d="M16.1676 16.168L12.8262 12.8266"
                  stroke="#9CA3AF"
                  strokeWidth="1.15486"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8.46782 14.6282C11.8695 14.6282 14.6271 11.8706 14.6271 8.46892C14.6271 5.06727 11.8695 2.30969 8.46782 2.30969C5.06617 2.30969 2.30859 5.06727 2.30859 8.46892C2.30859 11.8706 5.06617 14.6282 8.46782 14.6282Z"
                  stroke="#9CA3AF"
                  strokeWidth="1.15486"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search Document"
                className="signature-search-input"
              />
            </div>

            <div className="flex items-center gap-3 flex-wrap ml-auto">
              {/* Tabs in Middle */}
              
             
              {/* Status Filter - Only show for Sent tab */}

              <div
                className="signature-status-dropdown-wrapper"
                ref={dropdownRef}
              >
                <button
                  ref={buttonRef}
                  className="signature-status-button"
                  data-model-id="459:2256"
                  onClick={handleClick}
                  onKeyDown={handleKeyDown}
                  type="button"
                  aria-haspopup="true"
                  aria-expanded={isOpen}
                  aria-label="Filter by status"
                >
                  <div className="signature-status-button-text">
                    {statusFilter}
                  </div>

                  <img
                    className={` w-[18px] h-3.5 transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                    alt=""
                    src="https://c.animaapp.com/M9B8e3bP/img/icon.svg"
                    aria-hidden="true"
                  />
                </button>

                {isOpen && (
                  <div
                    className="signature-status-dropdown"
                    style={{
                      position: "absolute",
                      top: `${dropdownPosition.top}px`,
                      left: `${dropdownPosition.left}px`,
                    }}
                  >
                    {statusOptions.map((option, index) => (
                      <button
                        key={option}
                        className={`signature-status-option ${
                          statusFilter === option
                            ? "signature-status-option-active"
                            : ""
                        }`}
                        onClick={() => handleOptionClick(option)}
                        type="button"
                      >
                        <div className="signature-status-option-text">
                          {option}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <DateRangePicker
                onDateRangeChange={handleDateRangeChange}
                initialStartDate={startDate}
                initialEndDate={endDate}
              />
            </div>
          </header>

          {/* Tab Content */}
          <div className="signature-tab-content">
            {activeTab === "sent" && (
              <SendSignDocumentRequestTable
                searchTerm={searchTerm}
                statusFilter={statusFilter}
                startDate={startDate}
                endDate={endDate}
                pagination={pagination}
                onTotalCountChange={handleTotalCountChange}
                onLoadingChange={(loading) => {
                  if (loading) {
                    setIsOpen(false);
                  }
                }}
              />
            )}
            {activeTab === "inbox" && (
              <InboxTable
                searchTerm={searchTerm}
                startDate={startDate}
                endDate={endDate}
                statusFilter={statusFilter}
                pagination={pagination}
                onTotalCountChange={handleTotalCountChange}
                handleDateRangeChange={handleDateRangeChange}
              />
            )}
          </div>

          {/* Common Pagination */}
          {/* {totalCount > 0 && ( */}
            <div
              style={{
                marginTop: "1rem",
                padding: " 0 1.5rem",
                borderTop: "1.155px solid #E5E7EB",
              }}
            >
              <Pagination
                currentPage={pagination.pageNumber}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                pageSize={pagination.pageSize}
                resetPageSize={handleResetPageSize}
              />
            </div>
        </div>
      </div>
    </>
  );
}
